/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentScoreHeader from "./ContentScoreHeader";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/system";
import MainLoader from "../../../MainLoader/MainLoader";
import { ContentEditAnalysisViewDataGridStyle, ContentEditAnalysisViewStyle } from "./css";
import useQuery from "../../../../hooks/allQuery";
import { updateSelectedCompetitorsAddKeywords } from "../../../../gql/mutations/scrapy";
import { useMutation } from "@apollo/client";
import { COUNTRIES } from "../../NewQueryPopup";

const DEFAULT_COUNTRY = COUNTRIES.find(c => c.gl === "in");

function ContentEditAnalysisView({ competitors, queryId, refetchQuery }) {
  const isMblBreakpoint = useMediaQuery("(max-width: 820px)");
  const customizer = useSelector(state => state.customizer);
  const theme = useTheme();
  const defaultTop10 = (competitors || []).slice(0, 10).map(r => r.id);
  const [selectedRows, setSelectedRows] = useState(defaultTop10);
  const [loadingNext, setLoadingNext] = useState(false);
  const [widthLoader, setLoader] = useState(false);
  const [countries, setCountries] = useState([DEFAULT_COUNTRY]);
  const [UPDATE_SELECTED_COMPETITORS_ADD_KEYWORDS] = useMutation(updateSelectedCompetitorsAddKeywords);

  const allIds = competitors.map(r => r.id);
  const top10Ids = competitors.slice(0, 10).map(r => r.id);
  const { allQuery } = useQuery();

  const checkAll = selectedRows.length > 0 && allIds.every(id => selectedRows.includes(id));
  const checkTop10 = selectedRows.length === 10 && top10Ids.every(id => selectedRows.includes(id));
  const uncheckAll = selectedRows.length === 0;

  const wrapperRef = useRef(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);

  useEffect(() => {
    const handleResize = entries => {
      for (let entry of entries) {
        setLoader(true);
        setWrapperWidth(entry.contentRect.width);
        setTimeout(() => setLoader(false), 300);
      }
    };
    const observer = new ResizeObserver(handleResize);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => { if (wrapperRef.current) observer.unobserve(wrapperRef.current); };
  }, [wrapperRef]);

  useEffect(() => {
    setLoader(!widthLoader);
  }, [customizer?.isCollapse]);

  const handleCheckAll = () => setSelectedRows(checkAll ? [] : allIds);
  const handleCheckTop10 = () => setSelectedRows(checkTop10 ? [] : top10Ids);
  const handleUncheckAll = () => setSelectedRows([]);

  // Fires directly when Next is clicked — no intermediate dialog
  const onClickHandler = () => {
    setLoadingNext(true);
    allQuery({
      QUERY: UPDATE_SELECTED_COMPETITORS_ADD_KEYWORDS,
      variables: {
        data: {
          competitorIds: selectedRows,
          queryId: queryId,
          locations: countries.map(c => c.gl),
          loc_ids: countries.map(c => c.loc_id),
        },
      },
      onCompleteFunc: data => {
        setLoadingNext(false);
        // Refetch the content query — ContentEdit will see selectedByUser=true and render the editor
        if (refetchQuery) refetchQuery();
      },
      onErrorFunc: errdata => {
        console.log(errdata);
        setLoadingNext(false);
      },
    });
  };

  const columns = [
    {
      field: "id",
      headerName: "Select",
      width: 100,
      renderCell: params => (
        <Checkbox
          onClick={e => e.stopPropagation()}
          checked={selectedRows.includes(params.row.id)}
          onChange={e => {
            const isChecked = e.target.checked;
            setSelectedRows(prev =>
              isChecked ? [...prev, params.row.id] : prev.filter(id => id !== params.row.id)
            );
          }}
        />
      ),
    },
    {
      field: "googleRank",
      headerName: "Google Rank",
      width: 130,
      renderCell: params => (
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{params.value}</Typography>
      ),
    },
    {
      field: "contentScore",
      headerName: "Content Score",
      width: 150,
      renderCell: params => (
        <Typography
          sx={{
            backgroundColor: "#00CF00",
            color: "#fff",
            fontSize: "14px",
            fontWeight: 600,
            borderRadius: "4px",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "lengthWords",
      headerName: "Length (Words)",
      width: 150,
      renderCell: params => (
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{params.value}</Typography>
      ),
    },
    {
      field: "url",
      headerName: "URL",
      width: 250,
      flex: isMblBreakpoint ? undefined : 1,
      renderCell: params => (
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: theme.palette.primary.main,
            "& a": { color: theme.palette.primary.main },
          }}
        >
          <Link to={params.value} target="_blank" rel="noopener noreferrer" title={params.value} onClick={e => e.stopPropagation()}>
            {params.value}
          </Link>
        </Box>
      ),
    },
  ];

  return (
    <ContentEditAnalysisViewStyle>
      <Container maxWidth="">
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <Box ref={wrapperRef} sx={{ width: "100%" }}>
                <ContentScoreHeader selectedRows={selectedRows} onClickHandler={onClickHandler} loading={loadingNext} />
              </Box>
            </Grid>
          </Grid>
          {widthLoader ? (
            <MainLoader message="Loading..." />
          ) : (
            <Grid container spacing={2} mt={0} sx={{ justifyContent: "center" }}>
              <Grid item xs={12} lg={12}>
                <Card
                  className="TableHeadWrapper"
                  sx={{
                    padding: "16px",
                    border: `1px solid ${theme.palette.divider}`,
                    borderBottom: "none",
                    boxShadow: "none !important",
                    borderBottomLeftRadius: "0",
                    borderBottomRightRadius: "0",
                  }}
                >
                  <Typography component="p" sx={{ mb: 1.5 }}>
                    This choice affects the suggestions provided by the editor. You may want to select your true competitors and pages matching your expected content type. It is
                    advised to select at least 5 pages. (You will also be able to alter the selection later on - below the content score.)
                  </Typography>

                  {/* Inline location selector */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Target country
                      </Typography>
                      <Tooltip
                        title="Used for Google Keyword Planner to fetch local search volumes for your keywords. Defaults to India — change if you're targeting a different market."
                        placement="right"
                        arrow
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", cursor: "pointer", "&:hover": { color: "primary.main" } }} />
                      </Tooltip>
                    </Box>
                    <Autocomplete
                      multiple
                      options={COUNTRIES}
                      getOptionLabel={opt => opt.label}
                      value={countries}
                      onChange={(_, val) => setCountries(val.length ? val : [DEFAULT_COUNTRY])}
                      disableCloseOnSelect
                      size="small"
                      sx={{ minWidth: 220, maxWidth: 500 }}
                      renderInput={params => (
                        <TextField {...params} variant="outlined" size="small" placeholder={countries.length ? "" : "Select countries"} />
                      )}
                    />
                  </Box>

                  <FormGroup className="CheckboxWrapper">
                    <FormControlLabel control={<Checkbox checked={checkAll} />} label="Check all" onChange={handleCheckAll} />
                    <FormControlLabel control={<Checkbox checked={checkTop10} />} label="Check top 10" onChange={handleCheckTop10} />
                    <FormControlLabel control={<Checkbox checked={uncheckAll} />} label="Uncheck all" onChange={handleUncheckAll} />
                  </FormGroup>
                </Card>
                <ContentEditAnalysisViewDataGridStyle>
                  <Box className="DataGridWrapper" style={{ width: wrapperWidth }}>
                    <DataGrid rows={competitors} columns={columns} pageSize={false} pagination={false} className="datagridnopage" disableColumnMenu />
                  </Box>
                </ContentEditAnalysisViewDataGridStyle>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>
    </ContentEditAnalysisViewStyle>
  );
}

export default ContentEditAnalysisView;
